import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import React from 'react'
import Agenda from './agenda'
import './events_index.css'
const { useState, useCallback, useMemo, useEffect } = React

const localizer = momentLocalizer(moment)

const EventsIndex = (props) => {
  const initialEvents = useMemo(() => {
    if (!props.agency_programs) return []

    return props.agency_programs.flatMap((program) =>
      program.program_events.map((event) => ({
        id: event.id,
        title: event.event_name,
        program: program.name,
        programId: program.id,
        location: event.program_location?.location?.name,
        region: event.program_location?.location?.region?.name,
        county: event.program_location?.location?.county?.name,
        handicapAccessible: event.program_location?.location?.handicap_accessible,
        instructors: event.instructors || [], // Add instructors
        start: new Date(event.start_at),
        end: new Date(event.end_at),
        description: event.description,
        eventId: event.id,
        event_registrations_count: event.event_registrations_count,
        postal_code: event.program_location?.location?.postal_code,
        eventStatus: event.status,
      }))
    )
  }, [props.agency_programs])

  const [events] = useState(initialEvents)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [currentView, setCurrentView] = useState('month')
  const [selectedProgram, setSelectedProgram] = useState('all')
  const [handicapAccessibleFilter, setHandicapAccessibleFilter] = useState('all')
  const [eventStatusFilter, setEventStatusFilter] = useState('all')
  const [regionFilter, setRegionFilter] = useState('all')
  const [countyFilter, setCountyFilter] = useState('all')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [myEventsOnly, setMyEventsOnly] = useState(false)
  const [postalCode, setPostalCode] = useState('')
  const [radiusFilter, setRadiusFilter] = useState('10')
  const [postalCodeError, setPostalCodeError] = useState('')

  // Get events visible in current calendar view
  const visibleEvents = useMemo(() => {
    const viewStart = moment(currentDate).startOf(currentView)
    const viewEnd = moment(currentDate).endOf(currentView)

    // Extend range for month view to include partial weeks
    if (currentView === 'month') {
      viewStart.startOf('week')
      viewEnd.endOf('week')
    }

    return events.filter((event) => {
      const eventStart = moment(event.start)
      return eventStart.isBetween(viewStart, viewEnd, 'day', '[]')
    })
  }, [events, currentDate, currentView])

  // State for postal code search results
  const [postalCodeFilteredEvents, setPostalCodeFilteredEvents] = useState(null)
  const [isLoadingRadiusSearch, setIsLoadingRadiusSearch] = useState(false)

  // Perform postal code radius search when postal code and radius are provided
  useEffect(() => {
    // Cancel previous request if it exists
    let abortController = null

    // Only search when postal code is exactly 5 digits
    if (postalCode.trim().length === 5) {
      setIsLoadingRadiusSearch(true)

      // Get program IDs - use selected program or all programs if 'all' is selected
      const programIds =
        selectedProgram !== 'all'
          ? [selectedProgram]
          : (props.agency_programs || []).map((p) => p.id)

      // Get agency_id from the current URL path
      const pathParts = window.location.pathname.split('/')
      const agencyIndex = pathParts.indexOf('agencies')
      const agencyId = agencyIndex !== -1 ? pathParts[agencyIndex + 1] : null

      if (!agencyId) {
        console.error('Could not determine agency ID from URL')
        setIsLoadingRadiusSearch(false)
        return
      }

      // Build query string with program IDs as array
      const queryParams = new URLSearchParams()
      queryParams.append('search_args[location_search][location_string]', postalCode)
      queryParams.append('search_args[location_search][location_radius]', radiusFilter)
      programIds.forEach((id) => queryParams.append('search_args[program_ids][]', id))

      // Create abort controller for this request
      abortController = new AbortController()

      fetch(`/events/agencies/${agencyId}/calendar/radius_search?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal: abortController.signal,
      })
        .then((response) => {
          // Check if response is ok and not aborted
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          // Check if response is not from a cancelled request
          if (abortController.signal.aborted) {
            return
          }

          if (data.event_postal_codes) {
            setPostalCodeFilteredEvents(data.event_postal_codes)
          }
        })
        .catch((error) => {
          // Check if error is due to abort
          if (error.name !== 'AbortError') {
            console.error('Error fetching postal code search:', error)
            setPostalCodeError('Failed to fetch events from postal code. Please try again.')
          }
          // Ignore abort errors
        })
        .finally(() => {
          setIsLoadingRadiusSearch(false)
        })
    } else {
      // Clear results if postal code is not 5 digits
      setPostalCodeFilteredEvents(null)
      setIsLoadingRadiusSearch(false)
    }

    // Cleanup function to abort request if component unmounts or effect re-runs
    return () => {
      if (abortController) {
        abortController.abort()
      }
    }
  }, [selectedProgram, postalCode, radiusFilter, props.agency_programs])

  // Filter events based on selected filters (applied to visible events)
  const filteredEvents = useMemo(() => {
    let filtered = visibleEvents

    // Filter by program
    if (selectedProgram !== 'all') {
      filtered = filtered.filter((event) => event.programId === parseInt(selectedProgram))
    }

    // Filter by handicap accessibility
    if (handicapAccessibleFilter !== 'all') {
      const isAccessible = handicapAccessibleFilter === 'true'
      filtered = filtered.filter((event) => event.handicapAccessible === isAccessible)
    }

    // Filter by region
    if (regionFilter !== 'all') {
      filtered = filtered.filter((event) => event.region === regionFilter)
    }

    // Filter by county
    if (countyFilter !== 'all') {
      filtered = filtered.filter((event) => event.county === countyFilter)
    }

    // Filer by status
    if (eventStatusFilter !== 'all') {
      filtered = filtered.filter((event) => event.eventStatus === eventStatusFilter)
    }

    // Filter by postal code radius search results
    // If search was performed (not null), filter by results
    // If results are empty array, show no events
    if (postalCodeFilteredEvents !== null) {
      if (postalCodeFilteredEvents.length > 0) {
        filtered = filtered.filter((event) => postalCodeFilteredEvents.includes(event.postal_code))
      } else {
        // Search returned no results - show no events
        filtered = []
      }
    }

    // Filter by My Events
    if (myEventsOnly && props.current_user_email) {
      filtered = filtered.filter((event) =>
        event.instructors?.some(
          (instructor) => instructor.email_address === props.current_user_email
        )
      )
    }

    return filtered
  }, [
    visibleEvents,
    selectedProgram,
    handicapAccessibleFilter,
    regionFilter,
    countyFilter,
    eventStatusFilter,
    myEventsOnly,
    props.current_user_email,
    postalCodeFilteredEvents,
  ])

  // Handle view changes
  const handleViewChange = (view) => {
    setCurrentView(view)
  }

  // Toggle filters visibility
  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible)
  }

  const handleSelectSlot = useCallback(({ start, end }) => {
    //   const title = window.prompt('New Event name')
    //   if (title) {
    //     setEvents((prev) => [
    //       ...prev,
    //       {
    //         id: Date.now(),
    //         title,
    //         start,
    //         end,
    //       },
    //     ])
    //   }
  }, [])

  const handleSelectEvent = useCallback((event) => {
    const pathParts = window.location.pathname.split('/')
    const calendarIndex = pathParts.indexOf('calendar')
    if (calendarIndex !== -1) {
      pathParts.splice(calendarIndex, 1, 'programs', event.programId, 'events', event.eventId)
    }
    const newPath = pathParts.join('/')
    window.open(`${window.location.origin}${newPath}`, '_blank')
  }, [])

  return (
    <div className="mdl-grid" style={{ position: 'relative' }}>
      <div
        className="mdl-cell mdl-cell--12-col"
        style={{
          backgroundColor: '#fcfcfc',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          marginBottom: '5px',
        }}>
        <div className="mdl-grid">
          <div className="mdl-cell mdl-cell--12-col">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <p style={{ fontSize: 'initial', marginBottom: '0px', marginRight: '10px' }}>
                Filter Events
              </p>
              <button
                type="button"
                className="mdl-button mdl-button--icon mdl-button--transparent mr-0"
                onClick={toggleFilters}
                aria-label={filtersVisible ? 'Hide filters' : 'Show filters'}
                style={{
                  transform: filtersVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}>
                <i className="material-icons">expand_more</i>
              </button>
            </div>
            <hr style={{ margin: '0' }} />
          </div>

          {/* Conditionally render filters */}
          {filtersVisible && (
            <React.Fragment>
              {/* Program Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label htmlFor="program-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Filter by Program:
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="program-filter"
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '200px',
                  }}>
                  <option value="all">All Programs</option>
                  {(props.agency_programs || []).map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Region Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label htmlFor="region-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Filter by Region:
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="region-filter"
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '200px',
                  }}>
                  <option value="all">All Regions</option>
                  {(props.regions || []).map((region) => (
                    <option key={region.id} value={region.name}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* County Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label htmlFor="county-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Filter by County:
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="county-filter"
                  value={countyFilter}
                  onChange={(e) => setCountyFilter(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '200px',
                  }}>
                  <option value="all">All Counties</option>
                  {(props.counties || []).map((county) => (
                    <option key={county.id} value={county.name}>
                      {county.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Handicap Accessible Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label
                  htmlFor="handicap-filter"
                  style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Handicap Accessible:
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="handicap-filter"
                  value={handicapAccessibleFilter}
                  onChange={(e) => setHandicapAccessibleFilter(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '150px',
                  }}>
                  <option value="all">All Locations</option>
                  <option value="true">Accessible Only</option>
                  <option value="false">Not Accessible</option>
                </select>
              </div>

              {/* Event Status Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label htmlFor="status-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Status:
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="status-filter"
                  value={eventStatusFilter}
                  onChange={(e) => setEventStatusFilter(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '150px',
                  }}>
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="approved">Approved</option>
                  <option value="pending_activation">Pending Activation</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="pending_cancellation">Pending Cancellation</option>
                  <option value="canceled">Canceled</option>
                  <option value="transferred">Transferred</option>
                  <option value="activation_denied">Activation Denied</option>
                </select>
              </div>

              {/* Postal Code Field */}
              <div className="mdl-cell mdl-cell--4-col">
                <label
                  htmlFor="postal-code-filter"
                  style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  Postal Code:{' '}
                  {isLoadingRadiusSearch && (
                    <small style={{ color: '#949494' }}>&nbsp; searching...</small>
                  )}
                </label>
                <input
                  id="postal-code-filter"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={postalCode}
                  onChange={(e) => {
                    // Filter out non-numeric characters and limit to 5 digits
                    const numericValue = e.target.value.replace(/[^0-9]/g, '')
                    if (numericValue.length <= 5) {
                      setPostalCode(numericValue)
                      setPostalCodeError('') // Clear error when user types
                    }
                  }}
                  placeholder="Enter postal code"
                  maxLength="5"
                  className="postal-code-input"
                />
                {postalCodeError && (
                  <div style={{ color: 'red', fontSize: '0.8em', marginTop: '4px' }}>
                    {postalCodeError}
                  </div>
                )}
              </div>

              {/* Radius Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label
                  htmlFor="radius-filter"
                  style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  Radius (miles):
                </label>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="radius-filter"
                  value={radiusFilter}
                  onChange={(e) => setRadiusFilter(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '150px',
                  }}>
                  <option value="10">10 miles</option>
                  <option value="25">25 miles</option>
                  <option value="50">50 miles</option>
                  <option value="100">100 miles</option>
                  <option value="250">250 miles</option>
                </select>
              </div>

              {/* My Events Filter */}
              <div className="mdl-cell mdl-cell--4-col">
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    checked={myEventsOnly}
                    onChange={(e) => setMyEventsOnly(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  My Events
                </label>
              </div>
            </React.Fragment>
          )}
        </div>
      </div>

      <div className="mdl-cell mdl-cell--9-col mdl-cell--hide-tablet mdl-cell--hide-phone">
        <h4>Calendar</h4>
        <hr />
        <div style={{ height: 600 }}>
          <Calendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onNavigate={setCurrentDate}
            onView={handleViewChange}
            date={currentDate}
            selectable
            popup
            views={['month', 'week', 'day']}
            defaultView="month"
            components={{
              event: ({ event }) => (
                <span style={{ fontSize: 'smaller' }}>
                  {currentView === 'month' && moment(event.start).format('h:mm A')}
                  {currentView === 'month' && <br />}
                  <strong>{event.title}</strong>
                </span>
              ),
            }}
          />
        </div>
      </div>

      <div className="mdl-cell mdl-cell--3-col mdl-cell--8-col-tablet mdl-cell--8-col-phone">
        <h4>Events</h4>
        <hr />
        <Agenda events={filteredEvents} currentDate={currentDate} onNavigate={setCurrentDate} />
      </div>
    </div>
  )
}

export default EventsIndex
